"use client"

/**
 * Puck render island.
 *
 * Puck's <Render> is CSR-only and pulls in the entire uiBuilderConfig
 * (43+ block definitions — CKEditor, video, leaflet, etc.). Keeping it
 * in its own dynamic-loaded client chunk keeps public pages that don't
 * use Puck (and pages that do but haven't hit the block yet) from
 * paying the cost at first paint.
 */

import dynamic from "next/dynamic"
import type { CSSProperties, ReactNode } from "react"

// Dynamic load: the chunk only fetches when the island actually renders.
// `ssr: false` is correct here — Puck's render tree relies on browser
// APIs and can't run on the server.
const PuckRender = dynamic(
  () =>
    Promise.all([
      import("@measured/puck").then(m => m.Render),
      import("@/(admin)/modules/ui-builder/config/uiBuilderConfig").then(m => m.uiBuilderConfig),
    ]).then(([Render, config]) => {
      // Capture the Render component and bind the config eagerly so consumers
      // can pass `data` only.
      const BoundPuckRender = (props: { data: any }) => <Render config={config} data={props.data} />
      return BoundPuckRender
    }),
  {
    ssr: false,
    loading: () => <PuckFallback />,
  },
)

function PuckFallback(): ReactNode {
  // The reserved height lives on the wrapper in `PuckIsland` (a stable node
  // that survives the skeleton → real-content swap). The fallback just fills
  // it, so there's nothing to size here.
  return <div aria-busy="true" aria-live="polite" style={{ width: "100%", height: "100%" }} />
}

export default function PuckIsland({
  data,
  // Reserve a viewport's worth of vertical space by default. The island is
  // `ssr:false`, so the server emits an empty box; without a reserved height
  // the footer (and everything below) sits right under a 200 px placeholder
  // and gets shoved down ~thousands of px when the island hydrates — the
  // single biggest contributor to homepage CLS. Reserving `100vh` pushes the
  // below-island content below the fold so its post-hydration shift happens
  // (mostly) off-screen and no longer counts against CLS. F5 SSRs the real
  // content and removes the shift entirely; this is the cheap interim guard.
  minHeight = "100vh",
}: {
  data: unknown
  minHeight?: CSSProperties["minHeight"]
}) {
  if (!data) return null
  return (
    <div style={{ minHeight }}>
      <PuckRender data={data} />
    </div>
  )
}
